import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  getPendingSales,
  syncPendingSales,
  getProductCacheMeta,
  type PendingSale,
} from '../utils/offlineQueue';

export interface SyncResult {
  synced: number;
  failed: number;
  timestamp: Date;
}

export function useOfflineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSales, setPendingSales] = useState<PendingSale[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isCached, setIsCached] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState<SyncResult | null>(null);

  const pendingCount = pendingSales.length;

  const refreshPendingSales = useCallback(async () => {
    try {
      const sales = await getPendingSales();
      setPendingSales(sales);
    } catch {
      setPendingSales([]);
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    refreshPendingSales();
    const interval = setInterval(refreshPendingSales, 5000);
    return () => clearInterval(interval);
  }, [refreshPendingSales]);

  useEffect(() => {
    const checkCacheStatus = async () => {
      try {
        const meta = await getProductCacheMeta();
        setIsCached(meta !== null && meta.count > 0);
      } catch {
        setIsCached(false);
      }
    };

    checkCacheStatus();
  }, [isOnline]);

  // Prevent retry storm: 30s cooldown between auto-syncs
  const lastAutoSyncRef = useRef<number>(0);

  const syncNow = useCallback(async (): Promise<SyncResult> => {
    if (!navigator.onLine || isSyncing) {
      return { synced: 0, failed: 0, timestamp: new Date() };
    }
    setIsSyncing(true);
    const startCount = pendingSales.length;
    try {
      const result = await syncPendingSales();
      const syncResult: SyncResult = { ...result, timestamp: new Date() };
      setLastSyncResult(syncResult);
      await refreshPendingSales();

      // Show feedback toast
      if (result.synced > 0 && result.failed === 0) {
        toast.success(`${result.synced} offline sale${result.synced !== 1 ? 's' : ''} synced`);
      } else if (result.synced > 0 && result.failed > 0) {
        toast.info(`${result.synced} synced, ${result.failed} failed`);
      } else if (result.failed > 0 && startCount > 0) {
        const isStillOffline = !navigator.onLine;
        if (isStillOffline) {
          toast.info(`Will retry ${result.failed} sale${result.failed !== 1 ? 's' : ''} when connection is restored`);
        } else {
          toast.error(`${result.failed} offline sale${result.failed !== 1 ? 's' : ''} failed to sync`);
        }
      }

      return syncResult;
    } catch (err) {
      // Log unexpected errors from IndexedDB or other internal failures
      console.error('[OfflineSync] syncNow failed:', err);
      return { synced: 0, failed: startCount, timestamp: new Date() };
    } finally {
      setIsSyncing(false);
      lastAutoSyncRef.current = Date.now();
    }
  }, [isSyncing, pendingSales.length, refreshPendingSales]);

  // Auto-sync on reconnect (with 30s cooldown to prevent retry storms)
  useEffect(() => {
    const COOLDOWN_MS = 30_000;
    if (isOnline && pendingCount > 0 && !isSyncing) {
      const timeSinceLastSync = Date.now() - lastAutoSyncRef.current;
      if (timeSinceLastSync >= COOLDOWN_MS) {
        syncNow().catch(() => {});
      }
    }
  }, [isOnline, pendingCount, syncNow, isSyncing]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'sync-complete') {
        refreshPendingSales();
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', handleMessage);
  }, [refreshPendingSales]);

  return { isOnline, isCached, pendingCount, pendingSales, isSyncing, lastSyncResult, syncNow };
}
